import React, { Component, useEffect, useState, useRef } from "react";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { useLocation } from "react-router-dom";
import { useSnackbar } from "context/SnackbarContext";
import { useAlert } from "../context/AlertContext";
import { useLoading } from "context/LoadingContext";
import {
  fetchBarcodeDetails,
  uploadASN,
  uploadASNDocuments,
  checkASNDocumentUpload,
  fetchBoxNumber,
  ASNDataDownload,
} from "../Service/ApiService";
import MDInput from "components/MDInput";
import { Tooltip } from "@mui/material";
import { ErrorOutline } from "@mui/icons-material";
import MDBox from "components/MDBox";
// import { uniqueId } from "lodash";

// Define validation schema
const schema = Yup.object().shape({
  barcodes: Yup.array()
    .of(
      Yup.object().shape({
        barcode: Yup.string()
          .required("Barcode required")
          .matches(/^[A-Za-z0-9]+$/, "Invalid barcode"),
        quantity: Yup.number().nullable(),
        selected: Yup.number().oneOf([0, 1]),
      })
    )
    .min(1, "At least one barcode required"),
});

const REQUIRED_DOCUMENTS = [
  {
    key: "selfInspectionQualityChecklist",
    label: "Self-Inspection Quality Checklist",
    type: "self_inspection_quality_checklist",
  },
  {
    key: "packingSlip",
    label: "Packing Slip",
    type: "packing_slip",
  },
  {
    key: "invoiceCopy",
    label: "Invoice Copy",
    type: "invoice_copy",
  },
];

const ASNUploadController = () => {
  // Fetch vendor code from localstorage
  const vendorDetails = JSON.parse(localStorage.getItem("vendor"));
  const vendorCode = vendorDetails ? vendorDetails.vendorCode : null;

  const rowIdRef = useRef(1);
  const generateRowId = () => rowIdRef.current++;

  // Contexts
  const location = useLocation();
  const { setLoading } = useLoading();
  const { showSnackbar } = useSnackbar();
  const { showAlert } = useAlert();
  const { PONumber } = location.state || {};

  // Props
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [barcodes, setBarcodes] = useState("");
  const [errors, setErrors] = useState({});
  const [barcodesData, setBarcodesData] = useState([]);
  const [finalBarcodesData, setFinalBarcodesData] = useState([]);
  const [notFoundBarcodes, setNotFoundBarcodes] = useState([]);
  const [alreadyUploadedBarcodes, setAlreadyUploadedBarcodes] = useState([]);
  const [boxNumber, setBoxNumber] = useState(0);
  const [processedData, setProcessedData] = useState([]);

  // helper to check if all visible rows are selected
  // const allSelected = finalBarcodesData.length > 0 && finalBarcodesData.every((i) => i.selected);

  const allSelected = rows.length > 0 && rows.every((r) => r.selected);

  // Validation
  const validateField = async (fieldName, value) => {
    try {
      await Yup.reach(schema, fieldName).validate(value);
      setErrors((prev) => ({ ...prev, [fieldName]: null }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, [fieldName]: err.message }));
    }
  };

  const columnsFields = [
    {
      id: "select",
      Header: () => (
        <Tooltip title={allSelected ? "Unselect All" : "Select All"}>
          <input
            type="checkbox"
            aria-label="Select all"
            checked={allSelected}
            onChange={toggleAll}
          />
        </Tooltip>
      ),
      // eslint-disable-next-line react/prop-types
      Cell: ({ row }) => {
        // eslint-disable-next-line react/prop-types
        const rowId = row.original.rowId;
        // const matched = finalBarcodesData.find((i) => i.id === id);
        // const isChecked = Boolean(matched?.selected);
        // eslint-disable-next-line react/prop-types
        const isChecked = Boolean(row.original.selected);

        // eslint-disable-next-line react/prop-types
        const uploaded = row.original.uploaded;
        const tooltipTitle = uploaded ? "Disabled" : isChecked ? "Unselect" : "Select";

        return (
          <Tooltip title={uploaded ? "Disabled" : isChecked ? "Unselect" : "Select"}>
            <input
              type="checkbox"
              // eslint-disable-next-line react/prop-types
              checked={Boolean(row.original.selected)}
              onChange={() => toggleRow(rowId)}
              disabled={uploaded}
            />
          </Tooltip>
        );
      },
      disableSortBy: true,
      width: 40,
    },
    { Header: "Material Code", accessor: "materialCode", hidden: true },
    { Header: "Material Desc", accessor: "materialDesc", hidden: true },
    { Header: "Brand", accessor: "brand", hidden: true },
    { Header: "Product", accessor: "product", hidden: true },
    { Header: "Barcode", accessor: "barcode", hidden: true },
    {
      Header: "Total Quantity",
      accessor: "totalQuantity",
      align: "left",
      // eslint-disable-next-line react/prop-types
      Cell: ({ row }) => {
        return (
          <MDBox display="flex" alignItems="center" gap={1}>
            {/* eslint-disable-next-line react/prop-types */}
            {row.original.uploaded === 1 && (
              <Tooltip title="Already Uploaded">
                <ErrorOutline fontSize="medium" color="warning" />
              </Tooltip>
            )}

            <MDInput
              type="text"
              defaultValue={
                // use the original row value so "Total Quantity" stays constant
                // eslint-disable-next-line react/prop-types
                row.original.totalQuantity ?? 0
              }
              // eslint-disable-next-line react/prop-types
              disabled
            />
          </MDBox>
        );
      },
    },
    {
      Header: "Quantity",
      accessor: "quantity",
      align: "left",
      width: 150,
      // eslint-disable-next-line react/prop-types
      Cell: ({ row }) => {
        // eslint-disable-next-line react/prop-types
        const qty = row.original.quantity ?? 0;

        // const qty =
        //   // eslint-disable-next-line react/prop-types
        //   finalBarcodesData.find((i) => i.id === row.original.id)?.quantity ??
        //   // eslint-disable-next-line react/prop-types
        //   Math.min(row.original.quantity ?? 1, row.original.totalQuantity ?? 0);

        return (
          <MDBox display="flex" alignItems="center" gap={1}>
            {/* eslint-disable-next-line react/prop-types */}
            {row.original.uploaded === 1 && (
              <Tooltip title="Already Uploaded">
                <ErrorOutline fontSize="medium" color="warning" />
              </Tooltip>
            )}

            <MDInput
              // type="number"
              label="Enter Quantity"
              // use controlled value so UI reflects updates immediately
              value={qty}
              inputProps={{
                min: 0,
                // Max quantity must be total quantity + 10% of total quantity.
                // eslint-disable-next-line react/prop-types
                max: row.original.totalQuantity ? Math.floor(row.original.totalQuantity * 1.1) : 0,
                style: { width: "140px" },
              }}
              onChange={(e) => handleQuantityChange(e, row)}
              // eslint-disable-next-line react/prop-types
              disabled={row.original.uploaded}
            />
          </MDBox>
        );
      },
    },
  ];

  // Set box number
  useEffect(() => {
    getBoxNumber();
  }, []);

  useEffect(() => {
    setColumns(columnsFields);
  }, [allSelected]);

  useEffect(() => {
    console.log("finalBarcodesData updated", finalBarcodesData);
  }, [finalBarcodesData]);

  // Handle barcode change
  // const handleBarcodesChange = (e) => {
  //   const inputValue = e.target.value;

  //   // Append a comma at the end of newly typed or pasted content
  //   let processedValue = inputValue;
  //   if (!inputValue.endsWith(",") && !inputValue.endsWith("\n")) {
  //     processedValue += "\n";
  //   }

  //   processBarcodes(processedValue);
  // };
  const handleBarcodesChange = (e) => {
    let inputValue = e.target.value;

    // 🔑 Scanner ENTER behavior
    if (inputValue.endsWith("\n")) {
      inputValue = inputValue.trim();
      if (inputValue !== "") {
        inputValue += ", ";
      }
      processBarcodes(inputValue);
      e.target.value = ""; // 🔥 clear textbox like View-1
      return;
    }

    setBarcodes(inputValue);
  };


  // const handleBarcodesPaste = (e) => {
  //   e.preventDefault(); // prevent default browser paste

  //   const previousValue = e.target.value;
  //   const pastedText = e.clipboardData.getData("text");

  //   // Combine previous value + pasted text, and add a comma at the end
  //   const newValue = previousValue + pastedText + ",";
  //   setBarcodes(newValue);
  //   processBarcodes(newValue);
  // };

  const handleBarcodesPaste = (e) => {
    e.preventDefault();

    const pastedText = e.clipboardData.getData("text");

    // 🔑 Normalize pasted barcodes (space/newline → comma)
    let formatted = pastedText
      .trim()
      .replace(/\s+/g, ", ")
      .replace(/,+/g, ",");

    const combined = barcodes
      ? `${barcodes},${formatted}`
      : formatted;

    setBarcodes(combined);
    processBarcodes(combined);
  };


  const processBarcodes = (inputValue) => {
    console.log(inputValue);
    // const inputValue = value;
    setBarcodes(inputValue);
    validateField("barcodes", inputValue);

    let barcodesText = inputValue;

    // Prepare barcode objects from input string
    const rawBarcodes = barcodesText
      .split(",")
      .map((b) => b.trim())
      .filter((b) => b !== "");

    const rowsData = rawBarcodes.map((barcode, index) => {
      const existing = barcodesData[index] || rows[index];

      if (existing && existing.barcode === barcode) {
        return existing; // ✅ keep rowId, quantity, selected
      }

      return {
        rowId: generateRowId(), // ✅ integer, stable
        barcode,
        quantity: null,
        selected: 1,
      };
    });

    // Set barcode data as rowsData
    setBarcodesData(rowsData);

    // Handle scanner input (ends with Enter/newline)
    if (barcodesText.endsWith("\n")) {
      barcodesText = barcodesText.trim();
      if (barcodesText !== "") {
        barcodesText += ",";
      }
      setBarcodes(barcodesText);
    }
    setProcessedData(rowsData);
    console.log(inputValue);
    getBarcodes(rowsData);
  };

  // Clear barcodes
  const clearBarcodes = () => {
    setBarcodes("");
    setFinalBarcodesData([]);
    setRows([]);
    setErrors({});
    setAlreadyUploadedBarcodes([]);
    setNotFoundBarcodes([]);
  };

  const openDocumentUploadPopup = async () => {
    const popupHtml = `
      <div style="text-align:left; padding:2px 2px 0;">
        <div
          style="
            background: linear-gradient(135deg, #fff5f5 0%, #fff 100%);
            border: 1px solid #f3d4d1;
            border-radius: 14px;
            padding: 10px 14px;
            margin-bottom: 12px;
          "
        >
          <div style="font-size:14px; font-weight:700; color:#b71c1c; margin-bottom:2px;">
            Required Before ASN Submission
          </div>
          <div style="font-size:12px; color:#6b7280; line-height:1.4;">
            Upload all 3 mandatory documents to continue with ASN submit.
          </div>
        </div>

        <div style="display:grid; gap:8px;">
          ${REQUIRED_DOCUMENTS.map(
            (item) => `
              <div
                style="
                  background:#ffffff;
                  border:1px solid #e7e7e7;
                  border-radius:12px;
                  padding:10px 12px;
                  box-shadow:0 8px 24px rgba(15, 23, 42, 0.05);
                "
              >
                <label
                  for="${item.key}"
                  style="
                    display:block;
                    font-size:13px;
                    font-weight:700;
                    color:#1f2937;
                    margin-bottom:6px;
                  "
                >
                  ${item.label} <span style="color:#f44335;">*</span>
                </label>
                <div style="font-size:11px; color:#6b7280; margin-bottom:8px;">
                  Supported: PDF, JPG, PNG, DOC, DOCX, XLS, XLSX
                </div>
                <input
                  id="${item.key}"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                  style="
                    display:block;
                    width:100%;
                    padding:8px 10px;
                    border:1px dashed #d1d5db;
                    border-radius:10px;
                    background:#fafafa;
                    color:#374151;
                    box-sizing:border-box;
                  "
                />
              </div>
            `
          ).join("")}
        </div>
      </div>
    `;

    const result = await Swal.fire({
      title: "Upload Mandatory Documents",
      html: popupHtml,
      width: 640,
      padding: "1rem",
      showCancelButton: true,
      confirmButtonText: "Save & Submit",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#f44335",
      background: "#fffdfd",
      customClass: {
        popup: "asn-document-popup",
        title: "asn-document-popup-title",
        htmlContainer: "asn-document-popup-body",
        confirmButton: "asn-document-confirm-btn",
        cancelButton: "asn-document-cancel-btn",
      },
      buttonsStyling: true,
      focusConfirm: false,
      didOpen: () => {
        const popup = Swal.getPopup();
        if (!popup) {
          return;
        }

        popup.style.borderRadius = "20px";
        popup.style.margin = "0 auto";
        popup.style.boxShadow = "0 24px 60px rgba(15, 23, 42, 0.2)";
      },
      preConfirm: () => {
        const selectedDocuments = {};

        for (const item of REQUIRED_DOCUMENTS) {
          const inputElement = document.getElementById(item.key);
          const file = inputElement?.files?.[0];

          if (!file) {
            Swal.showValidationMessage(`${item.label} is required`);
            return false;
          }

          selectedDocuments[item.key] = file;
        }

        return selectedDocuments;
      },
    });

    if (!result.isConfirmed) {
      return null;
    }

    return result.value;
  };

  // getBarcodes
  const getBarcodes = async (rowsData = processedData) => {
    // console.log(processedData);
    await validateField("barcodes", rowsData);
    if (errors.barcodes) {
      return;
    }
    try {
      setLoading(true);
      const response = await fetchBarcodeDetails(vendorCode, PONumber, rowsData, boxNumber);

      if (response.success) {
        // const fetchedData = response.data.slice().reverse();
        const fetchedData = response.data;

        // Merge updated quantity and selected values from previous state
        const newData = fetchedData.map((item, index) => {
          const uiRow = rowsData[index];

          return {
            ...item,
            rowId: uiRow.rowId, // ✅ preserve UI identity
            quantity: uiRow.quantity ?? item.quantity,
            selected: uiRow.selected ?? item.selected,
          };
        });

        setRows(newData.filter((row) => row.uploaded !== 1));
        setFinalBarcodesData(newData);
        setAlreadyUploadedBarcodes(
          newData.filter((item) => item.uploaded === 1).map((item) => item.barcode)
        );
        setNotFoundBarcodes(response.notFound);
        setLoading(false);
      } else {
        setFinalBarcodesData(response.data);
        setRows(response.data);
        setNotFoundBarcodes(response.notFound);
        setLoading(false);
      }
    } catch (error) {
      showAlert("Something went wrong while fetching barcode data!");
      setLoading(false);
    }
  };

  const handleQuantityChange = (e, row) => {
    let newValue = Number(e.target.value);
    if (Number.isNaN(newValue) || newValue < 0) {
      newValue = 0;
    }

    const maxQty = Number(
      row.original.totalQuantity ? Math.floor(row.original.totalQuantity * 1.1) : 0
    );
    if (!Number.isNaN(maxQty) && newValue > maxQty) {
      newValue = maxQty;
    }

    const rowId = row.original.rowId;

    setRows((prev) => prev.map((r) => (r.rowId === rowId ? { ...r, quantity: newValue } : r)));

    setFinalBarcodesData((prev) =>
      prev.map((item) => (item.rowId === rowId ? { ...item, quantity: newValue } : item))
    );

    setBarcodesData((prev) =>
      prev.map((item) => (item.rowId === rowId ? { ...item, quantity: newValue } : item))
    );
  };

  const toggleAll = () => {
    const newVal = allSelected ? 0 : 1;

    setFinalBarcodesData((prev) => prev.map((i) => ({ ...i, selected: newVal })));

    setRows((prev) => prev.map((r) => ({ ...r, selected: newVal })));
  };

  // toggle single row
  // const toggleRow = (id) => {
  //   setFinalBarcodesData((prev) =>
  //     prev.map((i) => (i.id === id ? { ...i, selected: i.selected ? 0 : 1 } : i))
  //   );
  // };

  const toggleRow = (rowId) => {
    setRows((prev) =>
      prev.map((r) => (r.rowId === rowId ? { ...r, selected: r.selected ? 0 : 1 } : r))
    );

    setFinalBarcodesData((prev) =>
      prev.map((r) => (r.rowId === rowId ? { ...r, selected: r.selected ? 0 : 1 } : r))
    );
  };

  // Check if all the data is selected
  const checkDataSelected = () => finalBarcodesData.some((item) => item.selected === 1);

  // Upload ASN Data
  const handleASNUpload = async () => {
    if (!checkDataSelected()) {
      showAlert("No data selected for upload!", {
        icon: "info",
        title: "Info",
      });
      return;
    }

    let selectedDocuments = null;

    try {
      setLoading(true);
      const documentCheckResponse = await checkASNDocumentUpload(vendorCode, PONumber);
      const isDocumentAlreadyUploaded =
        String(documentCheckResponse?.isDocumentUpload || "").toLowerCase() === "yes";
      setLoading(false);

      if (!isDocumentAlreadyUploaded) {
        selectedDocuments = await openDocumentUploadPopup();
        if (!selectedDocuments) {
          return;
        }
      }
    } catch (error) {
      setLoading(false);
      showAlert("Something went wrong while checking document upload status!");
      return;
    }

    setLoading(true);
    try {
      if (selectedDocuments) {
        const documentsResponse = await uploadASNDocuments(vendorCode, PONumber, selectedDocuments);

        if (!documentsResponse?.success) {
          showAlert(documentsResponse?.msg || "Something went wrong while uploading documents!");
          return;
        }
      }

      const payload = {
        ASNData: finalBarcodesData,
        PONumber,
        vendorCode,
        boxNumber,
      };

      const response = await uploadASN(payload);

      if (response?.success) {
        const pdf = await ASNDataDownload(PONumber, vendorCode, boxNumber);

        showAlert("ASN Data successfully uploaded!", {
          icon: "success",
          title: "Success",
        });

        // Clear barcodes
        clearBarcodes();
        getBoxNumber();
      } else {
        showAlert(response?.msg || "Something went wrong while uploading ASN data!");
        getBoxNumber();
      }
    } catch (error) {
      showAlert("Something went wrong while uploading ASN data!");
      getBoxNumber();
    } finally {
      setLoading(false);
      getBoxNumber();
    }
  };

  // fetch latest unique box number
  const getBoxNumber = async () => {
    try {
      setLoading(true);
      const response = await fetchBoxNumber(vendorCode, PONumber);

      if (response.success) {
        setBoxNumber(response.data.boxNumber);
        setLoading(false);
      } else {
        setLoading(false);
        showAlert("Box number not assigned!");
      }
    } catch (error) {
      showAlert("Something went wrong while assigning box number!");
      setLoading(false);
    }
  };

  return {
    PONumber,
    boxNumber,
    rows,
    columns,
    barcodes,
    alreadyUploadedBarcodes,
    notFoundBarcodes,
    errors,
    handleBarcodesChange,
    handleBarcodesPaste,
    clearBarcodes,
    getBarcodes,
    handleQuantityChange,
    handleASNUpload,
  };
};

export default ASNUploadController;
